/* eslint-disable no-console */
/* eslint-disable max-lines */
import React, { useRef, useEffect, useState } from 'react';
import HandleButton from '@/components/HandleButton';
import { usePersistFn } from 'ahooks';
import { Modal } from 'antd-mobile';
import { useIntl, useSelector, useDispatch, type FosterStepModelsState, type UserState } from 'umi';
import { CloseOutlined } from '@ant-design/icons';
import { MoBaseContainer as BaseContainer } from '@gov-mo/components';
import HeaderProgress from '@/components/HeaderProgress';
import { stepInfo, StepType } from '@/utils/FosterServiceConf';
import { Page } from '@gov-mo/mpaas-js-bridge';
import GuideButton from '@/components/GuideButton';
import { calculateAge, formatDateToYYYYMMDD } from '@/utils/dateutils';
import { Helmet } from '@@/plugin-helmet/exports';
import { commonPostRequest, removeToken } from '@/utils/apiUtils';
import StepsPopover from './components/StepsPopover';
import styles from './css/FosterCareService.less';
import StepRender from './components/StepRender';
import useHandleStep from './components/StepRender/HandleStep';

function FosterCareService() {
  const dispatch = useDispatch();
  // 獲取國際化配置對象
  const intl = useIntl();

  const { profile } = useSelector(({ user }: { user: UserState }) => user);

  // 从models里取状态
  const { step, maxStep } = useSelector((x: { fosterStepModels: FosterStepModelsState }) => x.fosterStepModels);

  // 當前步驟
  const currentStep = stepInfo[step - 1];
  const [isValidated, setIsValidated] = useState(false);
  const [isMarried, setIsMarried] = useState(false);
  // 用于步骤切换时回到顶部操作
  const scrollRef = useRef<HTMLDivElement>(null);

  // 点击右上角"x"的时候，提示框的控制
  const goBackModal = useRef<{ close: () => void } | null>(null);

  // 更新/初始化models
  const updataModels = usePersistFn((payload) => {
    dispatch({
      type: 'fosterStepModels/update',
      payload,
    });
  });

  useEffect(() => {
    // 记录当前到过的最大步骤数，用于步骤间跳转
    if (maxStep < step) {
      updataModels({ maxStep: step });
    }
  }, [step]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [step]);

  useEffect(() => {
    if (!profile || !profile.euid) {
      setIsValidated(false);
      return;
    }
    // 調用接口，返回已婚未婚結果，未婚不允許下一步
    setIsMarried(profile.married);
    // 檢查是否有資格申請
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const result = validateAgeAndIdentity();
    setIsValidated(result);
  }, [profile]);

  // 澳門居民 25-65歲（本人），按周歲檢查
  const validateAgeAndIdentity = usePersistFn(() => {
    if (!profile || !profile.birthday || !profile.identityType) {
      return true; // 如果没有用户信息，则跳过检查
    }
    const { identityType, birthday } = profile;
    const age = calculateAge(birthday);
    if ((identityType !== '15' && identityType !== '14') || age < 25 || age > 65) {
      Modal.alert(
        <p style={{ fontWeight: '600', color: '#13a07b', fontSize: '16px', marginTop: '-15px', marginBottom: '10px' }}>
          {intl.formatMessage({ id: '溫馨提示' })}
        </p>,
        intl.formatMessage({ id: '台端的現況目前不符合申請成為寄養父母的基本條件。若需查詢請致電：83997703 /83997766' }),
        [
          {
            text: intl.formatMessage({ id: '知道了' }),
            onPress: () => {
              window.top.close();
            },
          },
        ]
      );
      return false;
    }
    return true;
  });

  /**
   * 上一步按鈕點擊
   */
  const handlePrevStep = usePersistFn(async () => {
    updataModels({ step: step - 1 });
  });

  /**
   * 退出时弹出的对话框方法
   */
  const handleGoBackModal = usePersistFn(() => {
    if (goBackModal.current) return;
    goBackModal.current = Modal.alert(
      intl.formatMessage({ id: '温馨提示' }),
      intl.formatMessage({ id: '确认放弃当前操作，并退出' }),
      [
        {
          text: intl.formatMessage({ id: '取消' }),
          onPress: () => {
            goBackModal.current?.close();
            goBackModal.current = null;
          },
        },
        {
          text: intl.formatMessage({ id: '确认' }),
          onPress: () => {
            removeToken();
            Page.close();
          },
        },
      ],
    );
  });

  /**
   * 下一步按鈕點擊
   */
  /* const handleNextStep = useHandleStep(currentStep); */
  const originalHandleNextStep = useHandleStep(currentStep);
  const handleNextStep = usePersistFn(async () => {
    if (!validateAgeAndIdentity()) {
      return; // 不满足条件时直接返回
    }
    // 判斷是否已婚
    if (!isMarried) { // 注意： 正式環境這裡要修改成 if (!isMarried)
      Modal.alert(
        <p style={{ fontWeight: '600', color: '#13a07b', fontSize: '16px', marginTop: '-15px', marginBottom: '10px' }}>
          {intl.formatMessage({ id: '溫馨提示' })}
        </p>,
        intl.formatMessage({ id: '台端的婚姻狀態不符合申請成為寄養父母的基本條件。若需查詢請致電：8399 7703 /8399 7766' }),
        [
          {
            text: intl.formatMessage({ id: '知道了' }),
            onPress: () => {
              window.top.close();
            },
          },
        ]
      );
      return; // 不满足条件时直接返回
    }
    // 否则调用原来的处理逻辑
    // eslint-disable-next-line consistent-return
    return originalHandleNextStep();
  });

  /**
   * 返回
   */
  const handleBackOrStepChange = usePersistFn(() => {
    // 第一步為返回并清空models

    if (step === 1) {
      handleGoBackModal();
    } else {
      // 其余为返回上一步
      handlePrevStep();
    }
  });

  /**
   * 底部按鈕文字
   */
  const renderButtonText = usePersistFn(() => {
    let text = '';
    switch (currentStep.stepType) {
      case StepType.dataCheck:
        text = intl.formatMessage({ id: '身份識別' });
        break;
      default:
        text = intl.formatMessage({ id: '下一步' });
    }
    return text;
  });

  // ... existing code ...

  return (
    isValidated && (
      <>
        <Helmet>
          <title>{intl.formatMessage({ id: '申請成為寄養父母' })}</title>
        </Helmet>
        <div className={styles.baseRoot}>
          <BaseContainer
            type="custom"
            bodyContentClass={styles.bodyContent}
            titleContent=""
            onLeftClick={handleBackOrStepChange as (param?: unknown) => void}
            // header 右上角申請步驟條按鈕&關閉按鈕
            rightContent={(
              <div className={styles.headerRightContent}>
                <StepsPopover stepList={stepInfo.map(i => i.stepName)} current={step} />
                <div onClick={handleGoBackModal} className={styles.iconArea}>
                  <CloseOutlined
                    className={styles.icon}
                    role="button"
                    aria-label={intl.formatMessage({ id: '關閉' })}
                  />
                </div>
              </div>
            )}
          >
            <div className={styles.root}>
              <div className={styles.main} ref={scrollRef}>
                {/* header 下的服務標題 */}
                <HeaderProgress
                  title={intl.formatMessage({ id: '申請成為寄養父母' })}
                  progressArr={stepInfo.map(i => i.stepName)}
                  current={step}
                />
                {/* 主要內容區域 */}
                <StepRender
                  currentStep={currentStep}
                  stepInfoList={stepInfo}
                  handleNext={handleNextStep}
                  mobile={false}
                />
              </div>

              {/* 右側的浮動圖標按鈕 */}
              <GuideButton />

              {/* 底部的上一步下一步按鈕 */}
              <HandleButton
                rightButton={{
                  text: renderButtonText(),
                  onClick: handleNextStep,
                }}
              />
            </div>
          </BaseContainer>
        </div>
      </>
    )
  );
}

export default FosterCareService;
